import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { AnimationName } from '@/entities/avatar-scenario/types/type';
import { useRealtimeAgentStore } from './realtime-agent';

export interface AvatarLoadingState {
  loadProgress: number;
  isAvatarReady: boolean;
  isThreejsAppReady: boolean;
  error: string | null;
  loadingAvatarType: string | null;
  currentAnimation: AnimationName | null;
  settingsVersion: number;
}

export interface AvatarActions {
  setIsThreejsAppReady: (isThreejsAppReady: boolean) => void;
  startLoading: (avatarType?: string) => void;
  updateProgress: (progress: number) => void;
  completeLoading: () => void;
  setError: (error: string) => void;
  reset: () => void;
  setCurrentAnimation: (animationName: AnimationName | null) => void;
  incrementSettingsVersion: () => void;
}

export type AvatarStore = AvatarLoadingState & AvatarActions;

const initialState: AvatarLoadingState = {
  loadProgress: 0,
  isAvatarReady: false,
  isThreejsAppReady: false,
  error: null,
  loadingAvatarType: null,
  currentAnimation: null,
  settingsVersion: 0,
};

export const useAvatarStore = create<AvatarStore>()(
  subscribeWithSelector(
    devtools(
      immer((set, get) => ({
        ...initialState,

        setIsThreejsAppReady: (isThreejsAppReady: boolean) => {
          set((state) => {
            state.isThreejsAppReady = isThreejsAppReady;
          }, false, 'avatar-loading/set-is-threejs-app-ready');
        },

        startLoading: (avatarType) => {
          set((state) => {
            state.isAvatarReady = false;
            state.loadProgress = 0;
            state.isThreejsAppReady = true;
            state.error = null;
            state.loadingAvatarType = avatarType || null;
          }, false, 'avatar-loading/start');
        },

        updateProgress: (progress) => {
          set((state) => {
            const clampedProgress = Math.max(0, Math.min(100, progress));
            state.loadProgress = clampedProgress;
          }, false, 'avatar-loading/update-progress');
        },

        completeLoading: () => {
          set((state) => {
            state.isAvatarReady = true;
            state.loadProgress = 100;
            state.error = null;
          }, false, 'avatar-loading/complete');

          // Cross-store notification: Realtime Agent Store에 아바타 로딩 완료 알림
          useRealtimeAgentStore.getState().setAvatarLoading(false);
        },

        setError: (error) => {
          set((state) => {
            state.isAvatarReady = false;
            state.error = error;
          }, false, 'avatar-loading/error');
        },

        reset: () => {
          set((state) => {
            Object.assign(state, initialState);
          }, false, 'avatar-loading/reset');
        },

        setCurrentAnimation: (animationName) => {
          if (animationName !== get().currentAnimation) {
            set((state) => {
              state.currentAnimation = animationName;
            }, false, 'avatar-loading/set-current-animation');
          }
        },

        incrementSettingsVersion: () => {
          set((state) => {
            state.settingsVersion += 1;
          }, false, 'avatar-loading/increment-settings-version');
        },
      })),
      {
        name: 'avatar-store',
      }
    ))
);